import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useProjectToday, nowOn } from '../../lib/projectToday';
import { heldNow } from '../../lib/retainage';

/* ──────────────────────────────────────────────────────────────────
 * BillingDialogs — Create / Compare / Release retainage modals
 *
 * All share the same shell:
 *  - Warm-tinted scrim (oklch(0.2 0.008 100 / 0.28)) + 2px backdrop-blur
 *  - 14px-radius card, 1px --border-default, layered shadow for depth
 *  - Header (18px / 20px / 14px) with title + subtitle + × close
 *  - Footer (12px / 20px / 16px) on --bg-surface-2 with primary/ghost
 *    button cluster
 *
 * Esc closes (unless inFlight).
 * ────────────────────────────────────────────────────────────────── */

const $f0 = (v) => '$' + (parseFloat(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const $f2 = (v) => '$' + (parseFloat(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const XIcon = ({ size = 16, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const Spinner = ({ size = 13, color = '#fff' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

const onFocusRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--accent)';
  e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)';
};
const onBlurRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--border-default)';
  e.currentTarget.style.boxShadow = 'none';
};

const inputStyle = {
  width: '100%', height: 34, padding: '0 10px',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)', borderRadius: 8,
  fontSize: 14.5, color: 'var(--text-1)',
  outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 500,
  color: 'var(--text-3)', textTransform: 'uppercase',
  letterSpacing: '0.05em', marginBottom: 6,
};

const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 36, padding: '0 14px', borderRadius: 8,
  fontSize: 14.5, fontWeight: 500, color: 'var(--text-2)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  cursor: 'pointer',
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 36, padding: '0 16px', borderRadius: 8,
  fontSize: 14.5, fontWeight: 500, color: '#fff',
  background: 'var(--accent)',
  border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
  boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
  cursor: 'pointer',
};

/* ─────────────────────── ModalShell ─────────────────────── */

const ModalShell = ({ open, onClose, title, subtitle, width = 480, children, disableClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !disableClose) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, disableClose]);
  if (!open) return null;
  return (
    <div
      onClick={() => !disableClose && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'oklch(0.2 0.008 100 / 0.28)',
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 'max(60px, 8vh)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width, maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 96px)',
          display: 'flex', flexDirection: 'column',
          background: 'var(--bg-surface)',
          borderRadius: 14,
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.18)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              margin: 0, fontSize: 17.5, fontWeight: 600,
              color: 'var(--text-1)', letterSpacing: '-0.018em',
            }}>{title}</h2>
            {subtitle && (
              <div style={{ fontSize: 13.5, color: 'var(--text-3)', marginTop: 3 }}>{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={() => !disableClose && onClose()}
            disabled={disableClose}
            aria-label="Close"
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: 'transparent', border: 'none',
              color: 'var(--text-3)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: disableClose ? 'not-allowed' : 'pointer',
              opacity: disableClose ? 0.4 : 1,
            }}
            onMouseEnter={(e) => { if (!disableClose) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
          >
            <XIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ─────────────────────── DateField (LastPass-proof) ───────────────────────
 * A custom date picker: a <button> trigger + a calendar popover portaled into
 * the .app container. Deliberately NOT a native <input type="date"> or text
 * input, so password managers (LastPass) have nothing to attach their fill
 * icon / credential dropdown to — the reported bug where LastPass covered the
 * calendar button and popped a login-fill box over the Invoice date field.
 *
 * value + onChange use 'YYYY-MM-DD' strings. All date math is LOCAL (parsed
 * from the string parts, never new Date('YYYY-MM-DD') which is UTC-midnight),
 * so there is no day-shift. Portaling into .app (not document.body) keeps the
 * warm-cream/sage tokens + .app.dark theme resolving correctly.
 * ────────────────────────────────────────────────────────────────────────── */

const _MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const _WD = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const _pad = (n) => String(n).padStart(2, '0');
const _parseYMD = (s) => { if (!/^\d{4}-\d{2}-\d{2}$/.test(s || '')) return null; const [y, m, d] = s.split('-').map(Number); return { y, m, d }; };
const _fmtYMD = (y, m, d) => `${y}-${_pad(m)}-${_pad(d)}`;
const _fmtDisplay = (s) => {
  const p = _parseYMD(s);
  if (!p) return 'Select date';
  return new Date(p.y, p.m - 1, p.d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const _daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const _firstWeekday = (y, m) => new Date(y, m - 1, 1).getDay();

const CalendarIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-3)', flexShrink: 0 }}>
    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const navBtnStyle = {
  width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)', color: 'var(--text-2)', cursor: 'pointer',
  fontSize: 16, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const DateField = ({ label, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const parsed = _parseYMD(value);
  const { testDate } = useProjectToday(); // an empty field opens on a test job's test date (2026-10-20)
  const now = nowOn(testDate);
  const [view, setView] = useState(() => (parsed ? { y: parsed.y, m: parsed.m } : { y: now.getFullYear(), m: now.getMonth() + 1 }));

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const CAL_H = 322, CAL_W = 256;
    let top = r.bottom + 6;
    if (top + CAL_H > window.innerHeight - 8) top = Math.max(8, r.top - CAL_H - 6);
    let left = r.left;
    if (left + CAL_W > window.innerWidth - 8) left = Math.max(8, window.innerWidth - CAL_W - 8);
    setPos({ top, left, width: CAL_W });
  };

  const openCal = () => { if (parsed) setView({ y: parsed.y, m: parsed.m }); place(); setOpen(true); };

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); if (btnRef.current) btnRef.current.focus(); } };
    const onReflow = () => place();
    document.addEventListener('mousedown', onDocDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open]);

  const pick = (day) => { onChange(_fmtYMD(view.y, view.m, day)); setOpen(false); if (btnRef.current) btnRef.current.focus(); };
  const shiftMonth = (delta) => setView((v) => {
    let m = v.m + delta; let y = v.y;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    return { y, m };
  });

  const host = (typeof document !== 'undefined' && (document.querySelector('.app') || document.body)) || null;
  const nDays = _daysInMonth(view.y, view.m);
  const lead = _firstWeekday(view.y, view.m);
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= nDays; d += 1) cells.push(d);

  // Keyboard operability: move focus into the calendar when it opens (the
  // popover is portaled to the end of .app, so it isn't in tab order after the
  // trigger), and let arrow keys move between days within the shown month.
  const focusDay = parsed ? parsed.d : now.getDate();
  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => {
      const grid = popRef.current;
      if (grid) {
        const target = grid.querySelector(`[data-day="${focusDay}"]`) || grid.querySelector('[data-day]');
        if (target) target.focus();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, focusDay]);
  const onGridKey = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const active = document.activeElement;
    const cur = Number(active && active.getAttribute && active.getAttribute('data-day')) || focusDay;
    let next = cur;
    if (e.key === 'ArrowLeft') next = cur - 1;
    else if (e.key === 'ArrowRight') next = cur + 1;
    else if (e.key === 'ArrowUp') next = cur - 7;
    else if (e.key === 'ArrowDown') next = cur + 7;
    next = Math.max(1, Math.min(nDays, next));
    const btn = popRef.current && popRef.current.querySelector(`[data-day="${next}"]`);
    if (btn) btn.focus();
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={labelStyle}>{label}</label>
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openCal())}
        style={{
          ...inputStyle,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
        }}
        onFocus={onFocusRing} onBlur={onBlurRing}
      >
        <span style={{ color: parsed ? 'var(--text-1)' : 'var(--text-4)' }}>{_fmtDisplay(value)}</span>
        <CalendarIcon />
      </button>
      {open && pos && host && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 10050,
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12,
            boxShadow: 'var(--shadow-md), 0 12px 32px -8px oklch(0.2 0.01 100 / 0.25)',
            padding: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" onClick={() => shiftMonth(-1)} style={navBtnStyle} aria-label="Previous month">‹</button>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>{_MONTHS[view.m - 1]} {view.y}</div>
            <button type="button" onClick={() => shiftMonth(1)} style={navBtnStyle} aria-label="Next month">›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
            {_WD.map((w, i) => <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-4)', padding: '2px 0' }}>{w}</div>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }} onKeyDown={onGridKey}>
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const isSel = parsed && parsed.y === view.y && parsed.m === view.m && parsed.d === d;
              const isToday = now.getFullYear() === view.y && now.getMonth() + 1 === view.m && now.getDate() === d;
              return (
                <button
                  key={i}
                  type="button"
                  data-day={d}
                  onClick={() => pick(d)}
                  style={{
                    height: 30, borderRadius: 7,
                    border: isToday && !isSel ? '1px solid var(--accent)' : '1px solid transparent',
                    background: isSel ? 'var(--accent)' : 'transparent',
                    color: isSel ? '#fff' : 'var(--text-1)',
                    fontSize: 13, fontFamily: 'var(--font-mono)', cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                >{d}</button>
              );
            })}
          </div>
        </div>,
        host,
      )}
    </div>
  );
};

/* ─────────────────────── CreatePayAppModal ─────────────────────── */

export const CreatePayAppModal = ({ open, onClose, onSubmit, defaultPayAppNum, submitting, errorMsg }) => {
  const { testDate } = useProjectToday(); // a test job's test date stands in for today (2026-10-20)
  const today = nowOn(testDate);
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  // Format from LOCAL calendar parts, NOT toISOString() (which is UTC and would
  // default the invoice date to tomorrow when opened in the evening in a US
  // timezone — the same day-shift DateField itself is built to avoid).
  const iso = (d) => _fmtYMD(d.getFullYear(), d.getMonth() + 1, d.getDate());

  const [form, setForm] = useState(() => ({
    period_name: defaultPayAppNum ? `AFP #${defaultPayAppNum}` : 'AFP #1',
    period_start_date: iso(firstOfMonth),
    period_end_date: iso(lastOfMonth),
    invoice_date: iso(today),
    notes: '',
  }));

  useEffect(() => {
    if (!open) return;
    setForm(prev => ({
      ...prev,
      period_name: defaultPayAppNum ? `AFP #${defaultPayAppNum}` : prev.period_name,
    }));
  }, [open, defaultPayAppNum]);

  const handleChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <ModalShell
      open={open} onClose={onClose}
      title="Create Pay Application"
      subtitle="Snapshot of current billing for one period."
      disableClose={submitting}
    >
      {/* Password-manager suppression. The date fields are a custom <DateField>
          (button + portaled calendar), NOT native inputs, so LastPass has
          nothing to attach its fill icon / login dropdown to — it was covering
          the calendar button and popping a credential-fill box over the Invoice
          date field. The remaining text inputs (period name, notes) keep
          autoComplete="off" + per-input opt-out attrs (data-lpignore etc.).
          (An earlier hidden username+password honeypot was removed — a password
          field made LastPass treat the whole modal as a login form.)

          onSubmit preventDefault keeps the Enter key from triggering a native
          form submission (the modal handles submit through its own Create button). */}
      <form
        autoComplete="off"
        onSubmit={(e) => { e.preventDefault(); }}
        style={{ padding: '14px 20px 6px', display: 'block' }}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Period name</label>
          <input
            type="text"
            name="b-period-name"
            value={form.period_name}
            onChange={(e) => handleChange('period_name', e.target.value)}
            placeholder="AFP #1 — May 2026"
            style={inputStyle}
            onFocus={onFocusRing} onBlur={onBlurRing}
            autoComplete="off"
            data-1p-ignore data-lpignore="true" data-form-type="other" data-bwignore="true"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <DateField label="Period start" value={form.period_start_date} onChange={(v) => handleChange('period_start_date', v)} />
          <DateField label="Period end" value={form.period_end_date} onChange={(v) => handleChange('period_end_date', v)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <DateField label="Invoice date" value={form.invoice_date} onChange={(v) => handleChange('invoice_date', v)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Notes (optional)</label>
          <textarea
            rows={3}
            name="b-notes"
            value={form.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Anything the AP team should know about this billing."
            style={{
              ...inputStyle,
              height: 'auto', minHeight: 70, padding: '8px 10px',
              resize: 'vertical', lineHeight: 1.45,
            }}
            onFocus={onFocusRing} onBlur={onBlurRing}
            autoComplete="off"
            data-1p-ignore data-lpignore="true" data-form-type="other" data-bwignore="true"
          />
        </div>
        {errorMsg && (
          <div style={{
            margin: '0 0 8px',
            padding: '8px 12px 8px 14px',
            background: 'var(--danger-soft)',
            borderRadius: 8,
            boxShadow: 'inset 3px 0 0 var(--danger)',
            border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)',
            color: 'var(--danger)', fontSize: 14,
          }}>{errorMsg}</div>
        )}
      </form>
      <div style={{
        padding: '12px 20px 16px',
        background: 'var(--bg-surface-2)',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
      }}>
        <button type="button" onClick={onClose} disabled={submitting} style={{ ...ghostBtn, opacity: submitting ? 0.45 : 1 }}>Cancel</button>
        <button
          type="button"
          onClick={() => onSubmit(form)}
          disabled={submitting}
          style={{ ...primaryBtn, opacity: submitting ? 0.85 : 1, cursor: submitting ? 'wait' : 'pointer' }}
        >
          {submitting ? <><Spinner /> Creating…</> : 'Create pay app'}
        </button>
      </div>
    </ModalShell>
  );
};

/* ─────────────────────── CompareModal (Review & Finalize) ─────────────────────── */

/**
 * Review-and-Finalize modal. Shown when the user clicks **Finalize**
 * in the footer of a Draft pay app. Surfaces the four totals the PM
 * needs to confirm before submitting (This Period / Mat. Stored /
 * Retainage Held / Ret. Released) plus the full G703 line table — so
 * nothing about the AFP changes between this preview and the commit.
 *
 * When a prior finalized period exists, the modal shows a Previous
 * Period / This Period / Difference triplet at the top AND extra
 * "Prev Period" + "Change" columns on the line table so the PM can
 * see what shifted from the last AFP.
 */
const Tile = ({ label, value, kind = 'neutral' }) => {
  const muted = !value || parseFloat(value) === 0;
  const palette = {
    neutral: { fg: 'var(--text-1)', bg: 'var(--bg-surface-2)', border: 'var(--border-subtle)' },
    accent:  { fg: 'var(--accent-text)', bg: 'var(--accent-soft)', border: 'color-mix(in oklch, var(--accent) 22%, transparent)' },
    warning: { fg: 'var(--warning)', bg: 'var(--warning-soft)', border: 'color-mix(in oklch, var(--warning) 22%, transparent)' },
    success: { fg: 'var(--success)', bg: 'var(--success-soft)', border: 'color-mix(in oklch, var(--success) 22%, transparent)' },
    danger:  { fg: 'var(--danger)',  bg: 'var(--danger-soft)',  border: 'color-mix(in oklch, var(--danger) 22%, transparent)' },
  }[kind];
  return (
    <div style={{
      flex: 1, minWidth: 140,
      padding: '12px 14px', borderRadius: 10,
      background: palette.bg,
      border: `1px solid ${palette.border}`,
    }}>
      <div style={{
        fontSize: 11.5, fontWeight: 600,
        color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em',
        marginBottom: 4,
      }}>{label}</div>
      <div className="mono" style={{
        fontSize: 19.5, fontWeight: 600,
        color: muted ? 'var(--text-4)' : palette.fg,
        letterSpacing: '-0.02em', lineHeight: 1.15,
      }}>{$f2(value)}</div>
    </div>
  );
};

/**
 * Revise dialog.
 *
 * Exists for one field. Revising used to be a single click that copied the
 * original's invoice date forward, so an August AFP revised in September still
 * claimed the August date -- which printed on the CE invoice and started the
 * net-30 clock from a day the owner never received it. The period covered does
 * NOT change (that is the work billed); the invoice date is when this revision
 * is actually issued, and it defaults to today.
 */
export const ReviseModal = ({ open, onClose, onSubmit, period, submitting, errorMsg }) => {
  const { testDate } = useProjectToday(); // a test job's test date stands in for today (2026-10-20)
  const iso = (d) => _fmtYMD(d.getFullYear(), d.getMonth() + 1, d.getDate());
  const [invoiceDate, setInvoiceDate] = useState(() => iso(nowOn(testDate)));
  useEffect(() => { if (open) setInvoiceDate(iso(nowOn(testDate))); }, [open]);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={`Revise ${period ? period.period_name : 'pay application'}`}
      subtitle="Creates a new Draft that supersedes this one"
      disableClose={submitting}
    >
      <div style={{ padding: '16px 20px 4px' }}>
        <DateField label="Invoice date" value={invoiceDate} onChange={setInvoiceDate} />
        {errorMsg ? (
          <div style={{
            margin: '12px 0 0', padding: '8px 12px 8px 14px',
            background: 'var(--danger-soft)', borderRadius: 8,
            boxShadow: 'inset 3px 0 0 var(--danger)',
            border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)',
            color: 'var(--danger)', fontSize: 14,
          }}>{errorMsg}</div>
        ) : null}
      </div>
      <div style={{
        padding: '12px 20px 16px', marginTop: 12,
        background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
      }}>
        <button type="button" onClick={onClose} disabled={submitting}
          style={{ ...ghostBtn, opacity: submitting ? 0.45 : 1 }}>Cancel</button>
        <button type="button" onClick={() => onSubmit({ invoice_date: invoiceDate })}
          disabled={submitting || !invoiceDate}
          style={{ ...primaryBtn, opacity: submitting ? 0.85 : 1, cursor: submitting ? 'wait' : 'pointer' }}>
          {submitting ? <><Spinner /> Creating&hellip;</> : 'Create revision'}
        </button>
      </div>
    </ModalShell>
  );
};

export const CompareModal = ({ open, onClose, current, prior, onFinalize, finalizing }) => {
  const num = (v) => parseFloat(v) || 0;
  const currentLines = current?.lines || [];
  const priorLines = prior?.lines || [];
  const priorMap = new Map(priorLines.map(l => [String(l.sov_line_id || l.id), l]));
  const hasPrior = !!prior && priorLines.length > 0;

  // Aggregate totals — G703 continuation shows gross completed & stored
  // (retainage is deducted on the G702 cover, not the continuation).
  const curTotal = currentLines.reduce((s, l) => s + num(l.total_completed_and_stored), 0);
  const prevTotal = priorLines.reduce((s, l) => s + num(l.total_completed_and_stored), 0);
  const curMatStored = currentLines.reduce((s, l) => s + num(l.stored_materials_this_period), 0);
  const curRetainage = currentLines.reduce((s, l) => s + heldNow(l), 0); // after releases, as the G702 shows it
  const curRetRelease = currentLines.reduce((s, l) => s + num(l.retainage_released_this_period), 0);
  const totalDelta = curTotal - prevTotal;

  // Line rows
  const rows = currentLines.map(l => {
    const p = priorMap.get(String(l.sov_line_id || l.id));
    const cur = num(l.total_completed_and_stored);
    const pri = p ? num(p.total_completed_and_stored) : 0;
    const delta = cur - pri;
    return {
      id: l.id,
      code: l.sov_code || l.item_number || '',
      desc: l.sov_description || l.description || '',
      pctComplete: num(l.current_percent_complete),
      sched: num(l.contract_amount),
      prior: pri,
      cur,
      delta,
      matStored: num(l.stored_materials_this_period),
      retainage: heldNow(l),
      retRelease: num(l.retainage_released_this_period),
      // AIA "Balance to Finish" = Scheduled Value - Total Completed &
      // Stored to date. current_percent_complete is the CUMULATIVE %
      // complete, so sched x (1 - pct/100) is the cleanest self-contained
      // computation. Clamped at 0 so a slightly-over line never shows
      // a negative "left to bill".
      leftToBill: Math.max(0, num(l.contract_amount) * (1 - num(l.current_percent_complete) / 100)),
    };
  });

  // Table grid: columns differ based on whether we're comparing to a prior
  // period. Trailing "Left to Bill" column added after Sched. Value.
  const colsWithPrior = '58px minmax(170px, 1fr) 54px 92px 92px 92px 92px 92px 92px 100px 100px';
  const colsNoPrior   = '58px minmax(170px, 1fr) 54px 92px 92px 92px 92px 100px 100px';

  return (
    <ModalShell
      open={open} onClose={onClose} width={hasPrior ? 1180 : 1020}
      title="Review & Finalize Billing"
      subtitle={
        hasPrior
          ? `Comparing "${current?.period_name || `AFP #${current?.id}`}" against "${prior?.period_name || `AFP #${prior?.id}`}"`
          : 'First billing period — no previous data to compare'
      }
      disableClose={finalizing}
    >
      <div style={{ padding: '16px 20px 8px', overflow: 'auto', maxHeight: '70vh' }}>
        {/* Top row — Period totals (with optional prior + delta) */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          {hasPrior && (
            <Tile label="Previous Period" value={prevTotal} kind="neutral" />
          )}
          <Tile label="This Period" value={curTotal} kind="accent" />
          {hasPrior && (
            <div style={{
              flex: 1, minWidth: 140,
              padding: '12px 14px', borderRadius: 10,
              background: totalDelta >= 0 ? 'var(--success-soft)' : 'var(--danger-soft)',
              border: `1px solid color-mix(in oklch, ${totalDelta >= 0 ? 'var(--success)' : 'var(--danger)'} 22%, transparent)`,
            }}>
              <div style={{
                fontSize: 11.5, fontWeight: 600,
                color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em',
                marginBottom: 4,
              }}>Difference</div>
              <div className="mono" style={{
                fontSize: 19.5, fontWeight: 600,
                color: totalDelta >= 0 ? 'var(--success)' : 'var(--danger)',
                letterSpacing: '-0.02em', lineHeight: 1.15,
              }}>{totalDelta >= 0 ? '+' : ''}{$f2(totalDelta)}</div>
            </div>
          )}
        </div>

        {/* Second row — Mat. Stored / Retainage Held / Ret. Released */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <Tile label="Mat. Stored" value={curMatStored} kind="neutral" />
          <Tile label="Retainage Held" value={curRetainage} kind="warning" />
          <Tile label="Ret. Released" value={curRetRelease} kind="success" />
        </div>

        {/* Line table */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: hasPrior ? colsWithPrior : colsNoPrior,
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            {[
              { label: 'Code', align: 'left' },
              { label: 'Description', align: 'left' },
              { label: '% Comp.', align: 'right' },
              ...(hasPrior ? [{ label: 'Prev Period', align: 'right' }] : []),
              { label: 'This Period', align: 'right' },
              ...(hasPrior ? [{ label: 'Change', align: 'right' }] : []),
              { label: 'Mat. Stored', align: 'right' },
              { label: 'Retainage', align: 'right' },
              { label: 'Ret. Release', align: 'right' },
              { label: 'Sched. Value', align: 'right' },
              { label: 'Left to Bill', align: 'right' },
            ].map((h, i, arr) => (
              <div key={h.label} style={{
                padding: '8px 10px',
                fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                textAlign: h.align,
                borderRight: i < arr.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                whiteSpace: 'nowrap',
              }}>{h.label}</div>
            ))}
          </div>

          {/* Rows */}
          {rows.length === 0 ? (
            <div style={{ padding: '24px 12px', textAlign: 'center', fontSize: 14, color: 'var(--text-4)' }}>
              No SOV lines on this pay application.
            </div>
          ) : (
            rows.map((r, i) => {
              const isLast = i === rows.length - 1;
              const deltaColor = r.delta > 0.01 ? 'var(--success)' : r.delta < -0.01 ? 'var(--danger)' : 'var(--text-4)';
              const rowHighlight = hasPrior && Math.abs(r.delta) > 0.01;
              const cellBase = (align = 'right') => ({
                padding: '8px 10px',
                fontSize: 13.5, color: 'var(--text-1)',
                textAlign: align,
                borderBottom: !isLast ? '1px solid var(--border-subtle)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              });
              return (
                <div
                  key={r.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: hasPrior ? colsWithPrior : colsNoPrior,
                    background: rowHighlight ? 'color-mix(in oklch, var(--warning-soft) 60%, var(--bg-surface))' : 'var(--bg-surface)',
                  }}
                >
                  <div className="mono" style={{ ...cellBase('left'), fontSize: 13, fontWeight: 500, color: 'var(--accent-text)' }}>
                    {r.code || '–'}
                  </div>
                  <div style={cellBase('left')}>{r.desc || '–'}</div>
                  <div className="mono" style={{
                    ...cellBase('right'),
                    fontSize: 13,
                    color: r.pctComplete === 0 ? 'var(--text-4)' : 'var(--text-1)',
                    letterSpacing: '-0.02em',
                  }}>
                    {r.pctComplete.toFixed(1)}%
                  </div>
                  {hasPrior && (
                    <div className="mono" style={{
                      ...cellBase('right'),
                      fontSize: 13, letterSpacing: '-0.02em',
                      color: r.prior === 0 ? 'var(--text-4)' : 'var(--text-3)',
                    }}>{$f2(r.prior)}</div>
                  )}
                  <div className="mono" style={{
                    ...cellBase('right'),
                    fontSize: 13, fontWeight: 600, letterSpacing: '-0.02em',
                    color: r.cur === 0 ? 'var(--text-4)' : 'var(--text-1)',
                  }}>{$f2(r.cur)}</div>
                  {hasPrior && (
                    <div className="mono" style={{
                      ...cellBase('right'),
                      fontSize: 13, fontWeight: 500, letterSpacing: '-0.02em',
                      color: deltaColor,
                    }}>{Math.abs(r.delta) > 0.01 ? ((r.delta > 0 ? '+' : '') + $f2(r.delta)) : '–'}</div>
                  )}
                  <div className="mono" style={{
                    ...cellBase('right'),
                    fontSize: 13, letterSpacing: '-0.02em',
                    color: r.matStored === 0 ? 'var(--text-4)' : 'var(--text-1)',
                  }}>{$f2(r.matStored)}</div>
                  <div className="mono" style={{
                    ...cellBase('right'),
                    fontSize: 13, letterSpacing: '-0.02em',
                    color: r.retainage > 0 ? 'var(--warning)' : 'var(--text-4)',
                  }}>{$f2(r.retainage)}</div>
                  <div className="mono" style={{
                    ...cellBase('right'),
                    fontSize: 13, letterSpacing: '-0.02em',
                    color: r.retRelease > 0 ? 'var(--success)' : 'var(--text-4)',
                  }}>{$f2(r.retRelease)}</div>
                  <div className="mono" style={{
                    ...cellBase('right'),
                    fontSize: 13, letterSpacing: '-0.02em',
                    color: r.sched === 0 ? 'var(--text-4)' : 'var(--text-3)',
                  }}>{$f2(r.sched)}</div>
                  <div className="mono" style={{
                    ...cellBase('right'),
                    fontSize: 13, letterSpacing: '-0.02em',
                    // Fully billed (nothing left) reads muted; outstanding
                    // balance reads in normal text so it's scannable.
                    color: r.leftToBill < 0.01 ? 'var(--text-4)' : 'var(--text-1)',
                  }}>{$f2(r.leftToBill)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <div style={{
        padding: '12px 20px 16px',
        background: 'var(--bg-surface-2)',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
      }}>
        <button
          type="button"
          onClick={onClose}
          disabled={finalizing}
          style={{ ...ghostBtn, opacity: finalizing ? 0.45 : 1 }}
        >Go Back &amp; Edit</button>
        {onFinalize && (
          <button
            type="button"
            onClick={onFinalize}
            disabled={finalizing}
            style={{ ...primaryBtn, opacity: finalizing ? 0.85 : 1, cursor: finalizing ? 'wait' : 'pointer' }}
          >
            {finalizing ? <><Spinner /> Finalizing…</> : 'Finalize & Send to Accounting'}
          </button>
        )}
      </div>
    </ModalShell>
  );
};

/* ─────────────────────── ReleaseRetainageModal ─────────────────────── */

export const ReleaseRetainageModal = ({ open, payload, onClose, onSubmit, submitting }) => {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && payload) {
      setAmount(payload.alreadyReleased > 0 ? payload.alreadyReleased.toFixed(2) : '');
      setNotes('');
    }
  }, [open, payload]);

  if (!payload) return null;
  const totalHeld = payload.totalHeld || 0;
  const pctOfCompleted = payload.pctOfCompleted || '5.0';

  return (
    <ModalShell
      open={open} onClose={onClose}
      title="Release Retainage"
      subtitle={`${payload.sovCode}${payload.lineDesc ? ' · ' + payload.lineDesc : ''}`}
      disableClose={submitting}
    >
      <div style={{ padding: '14px 20px 6px' }}>
        {/* Held amount panel */}
        <div style={{
          padding: '12px 14px',
          background: 'var(--warning-soft)',
          border: '1px solid color-mix(in oklch, var(--warning) 22%, transparent)',
          borderRadius: 10,
          marginBottom: 12,
        }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: 'var(--warning)',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
          }}>Retainage held</div>
          <div className="mono" style={{
            fontSize: 23.5, fontWeight: 600, color: 'var(--warning)',
            letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>{$f2(totalHeld)}</div>
          <div style={{ fontSize: 13, color: 'var(--warning)', opacity: 0.85, marginTop: 2 }}>
            {pctOfCompleted}% of completed
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...labelStyle, margin: 0 }}>Amount to release</label>
            <button
              type="button"
              onClick={() => setAmount(totalHeld.toFixed(2))}
              style={{
                background: 'transparent', border: 'none',
                fontSize: 12.5, fontWeight: 500, color: 'var(--accent-text)',
                cursor: 'pointer', padding: 0,
              }}
            >Release all</button>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', height: 34,
            background: 'var(--bg-surface)',
            border: '1.5px solid var(--accent)',
            borderRadius: 8, padding: '0 10px',
            boxShadow: '0 0 0 3px var(--accent-ring)',
          }}>
            <span style={{ color: 'var(--text-3)', fontSize: 14.5, marginRight: 6 }}>$</span>
            <input
              type="number" step="0.01" min="0" max={totalHeld}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              style={{
                flex: 1, minWidth: 0,
                background: 'transparent', border: 'none', outline: 'none',
                fontFamily: 'var(--font-mono)', fontSize: 14.5, color: 'var(--text-1)',
                letterSpacing: '-0.02em',
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <label style={labelStyle}>Notes (optional)</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why is retainage being released for this line?"
            style={{
              ...inputStyle,
              height: 'auto', minHeight: 70, padding: '8px 10px',
              resize: 'vertical', lineHeight: 1.45,
            }}
            onFocus={onFocusRing} onBlur={onBlurRing}
          />
        </div>
      </div>
      <div style={{
        padding: '12px 20px 16px',
        background: 'var(--bg-surface-2)',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
      }}>
        <button type="button" onClick={onClose} disabled={submitting} style={{ ...ghostBtn, opacity: submitting ? 0.45 : 1 }}>Cancel</button>
        <button
          type="button"
          onClick={() => onSubmit({ amount: parseFloat(amount) || 0, notes })}
          disabled={submitting || !amount}
          style={{
            ...primaryBtn,
            opacity: (submitting || !amount) ? 0.45 : 1,
            cursor: submitting ? 'wait' : (!amount ? 'not-allowed' : 'pointer'),
          }}
        >
          {submitting ? <><Spinner /> Releasing…</> : 'Release'}
        </button>
      </div>
    </ModalShell>
  );
};
